import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  McCard,
  McButton,
  McInput,
  McIcon,
  McSelect,
  McDialog,
  McNotification,
  McLoadingIndicator,
  McTag,
} from '@maersk-global/mds-react-wrapper';
import { registryService } from '../services/registryService';
import { queryService } from '../services/queryService';
import { DbServer, DbConnection, CreateDbServerRequest, CreateDbConnectionRequest } from '../types/registry';
import { useAuth } from '../context/AuthContext';
import { roleConfig } from '../config/roles';
import FullScreenLoader from '../components/Common/FullScreenLoader';

type NotificationType = 'success' | 'error' | 'info';

export default function ServerRegistryPage() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const canManage = hasRole(roleConfig.manageServerRegistry);

  // ─── State ─────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null);
  const [showServerDialog, setShowServerDialog] = useState(false);
  const [showConnectionDialog, setShowConnectionDialog] = useState(false);
  const [editingServer, setEditingServer] = useState<DbServer | null>(null);
  const [editingConnection, setEditingConnection] = useState<DbConnection | null>(null);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: NotificationType;
  }>({ show: false, message: '', type: 'info' });

  // Server form
  const [serverForm, setServerForm] = useState<CreateDbServerRequest>({
    serverName: '',
    applicationId: '',
    serverType: 'POSTGRESQL',
    host: '',
    port: 5432,
    sslEnabled: false,
    sslMode: 'disable',
    ownerTeam: '',
    description: '',
    tags: [],
  });

  // Connection form
  const [connectionForm, setConnectionForm] = useState<CreateDbConnectionRequest>({
    serverId: '',
    connectionName: '',
    databaseName: '',
    schemaName: '',
    credentialType: 'ENV',
    credentialRef: '',
    minPoolSize: 0,
    maxPoolSize: 3,
    allowedOps: 'READ_ONLY',
    description: '',
  });

  // ─── Queries ───────────────────────────────────────────────
  const {
    data: servers = [],
    isLoading: isLoadingServers,
    error: serversError,
  } = useQuery({
    queryKey: ['db-servers'],
    queryFn: () => registryService.getServers(true),
  });

  const { data: applications = [] } = useQuery({
    queryKey: ['applications'],
    queryFn: () => queryService.getApplications(),
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: connections = [],
    isLoading: isLoadingConnections,
  } = useQuery({
    queryKey: ['db-connections', selectedServerId],
    queryFn: () =>
      selectedServerId
        ? registryService.getConnectionsByServer(selectedServerId)
        : registryService.getConnections(true),
    enabled: true,
  });

  // ─── Mutations ─────────────────────────────────────────────
  const createServerMutation = useMutation({
    mutationFn: (req: CreateDbServerRequest) => registryService.createServer(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['db-servers'] });
      setShowServerDialog(false);
      showNotification('Server created successfully', 'success');
    },
    onError: (err: any) => showNotification(err?.message || 'Failed to create server', 'error'),
  });

  const updateServerMutation = useMutation({
    mutationFn: ({ id, req }: { id: string; req: CreateDbServerRequest }) =>
      registryService.updateServer(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['db-servers'] });
      setShowServerDialog(false);
      setEditingServer(null);
      showNotification('Server updated successfully', 'success');
    },
    onError: (err: any) => showNotification(err?.message || 'Failed to update server', 'error'),
  });

  const disableServerMutation = useMutation({
    mutationFn: (id: string) => registryService.disableServer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['db-servers'] });
      showNotification('Server disabled', 'success');
    },
    onError: (err: any) => showNotification(err?.message || 'Failed to disable server', 'error'),
  });

  const createConnectionMutation = useMutation({
    mutationFn: (req: CreateDbConnectionRequest) => registryService.createConnection(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['db-connections'] });
      setShowConnectionDialog(false);
      showNotification('Connection created successfully', 'success');
    },
    onError: (err: any) => showNotification(err?.message || 'Failed to create connection', 'error'),
  });

  const updateConnectionMutation = useMutation({
    mutationFn: ({ id, req }: { id: string; req: CreateDbConnectionRequest }) =>
      registryService.updateConnection(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['db-connections'] });
      setShowConnectionDialog(false);
      setEditingConnection(null);
      showNotification('Connection updated successfully', 'success');
    },
    onError: (err: any) => showNotification(err?.message || 'Failed to update connection', 'error'),
  });

  const disableConnectionMutation = useMutation({
    mutationFn: (id: string) => registryService.disableConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['db-connections'] });
      showNotification('Connection disabled', 'success');
    },
    onError: (err: any) => showNotification(err?.message || 'Failed to disable connection', 'error'),
  });

  // ─── Helpers ───────────────────────────────────────────────
  const showNotification = (message: string, type: NotificationType) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification((n) => ({ ...n, show: false })), 4000);
  };

  const filteredServers = useMemo(() => {
    if (!searchTerm) return servers;
    const term = searchTerm.toLowerCase();
    return servers.filter(
      (s) =>
        s.serverName.toLowerCase().includes(term) ||
        (s.applicationId || '').toLowerCase().includes(term) ||
        s.host.toLowerCase().includes(term) ||
        s.serverType.toLowerCase().includes(term) ||
        s.ownerTeam.toLowerCase().includes(term)
    );
  }, [servers, searchTerm]);

  const selectedServerConnections = useMemo(() => {
    if (selectedServerId) {
      return connections.filter((c) => c.serverId === selectedServerId);
    }
    return connections;
  }, [connections, selectedServerId]);

  const openCreateServer = () => {
    setEditingServer(null);
    setServerForm({
      serverName: '',
      applicationId: '',
      serverType: 'POSTGRESQL',
      host: '',
      port: 5432,
      sslEnabled: false,
      sslMode: 'disable',
      ownerTeam: '',
      description: '',
      tags: [],
    });
    setShowServerDialog(true);
  };

  const openEditServer = (server: DbServer) => {
    setEditingServer(server);
    setServerForm({
      serverName: server.serverName,
      applicationId: server.applicationId || '',
      serverType: server.serverType,
      host: server.host,
      port: server.port,
      sslEnabled: server.sslEnabled,
      sslMode: server.sslMode,
      sslCertRef: server.sslCertRef,
      ownerTeam: server.ownerTeam,
      description: server.description || '',
      tags: server.tags,
    });
    setShowServerDialog(true);
  };

  const openCreateConnection = (serverId?: string) => {
    setEditingConnection(null);
    setConnectionForm({
      serverId: serverId || selectedServerId || '',
      connectionName: '',
      databaseName: '',
      schemaName: '',
      credentialType: 'ENV',
      credentialRef: '',
      minPoolSize: 0,
      maxPoolSize: 3,
      allowedOps: 'READ_ONLY',
      description: '',
    });
    setShowConnectionDialog(true);
  };

  const openEditConnection = (conn: DbConnection) => {
    setEditingConnection(conn);
    setConnectionForm({
      serverId: conn.serverId,
      connectionName: conn.connectionName,
      databaseName: conn.databaseName,
      schemaName: conn.schemaName || '',
      credentialType: conn.credentialType,
      credentialRef: conn.credentialRef,
      minPoolSize: conn.minPoolSize,
      maxPoolSize: conn.maxPoolSize,
      allowedOps: conn.allowedOps,
      description: conn.description || '',
    });
    setShowConnectionDialog(true);
  };

  const handleSaveServer = () => {
    if (editingServer) {
      updateServerMutation.mutate({ id: editingServer.id, req: serverForm });
    } else {
      createServerMutation.mutate(serverForm);
    }
  };

  const handleSaveConnection = () => {
    if (editingConnection) {
      updateConnectionMutation.mutate({ id: editingConnection.id, req: connectionForm });
    } else {
      createConnectionMutation.mutate(connectionForm);
    }
  };

  // ─── Render ────────────────────────────────────────────────
  if (isLoadingServers) return <FullScreenLoader />;
  if (serversError) {
    return (
      <McNotification
        appearance="error"
        heading="Error loading servers"
        body={(serversError as Error).message}
      />
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Notification */}
      {notification.show && (
        <McNotification
          appearance={notification.type}
          heading={notification.type === 'error' ? 'Error' : 'Success'}
          body={notification.message}
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Server Registry</h1>
          <p style={{ margin: '4px 0 0', color: '#666' }}>
            Manage external database servers and connections
          </p>
        </div>
        {canManage && (
          <McButton appearance="primary" click={openCreateServer}>
            <McIcon icon="plus" slot="icon" />
            Add Server
          </McButton>
        )}
      </div>

      {/* Search */}
      <McInput
        placeholder="Search servers by name, host, type, or team..."
        value={searchTerm}
        onInput={(e: any) => setSearchTerm(e.target?.value ?? e.detail?.value ?? '')}
        style={{ marginBottom: '24px', maxWidth: '400px' }}
      >
        <McIcon icon="magnifying-glass" slot="prefix" />
      </McInput>

      {/* Two-panel layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Left: Servers */}
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>
            Servers ({filteredServers.length})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filteredServers.map((server) => (
              <McCard
                key={server.id}
                style={{
                  cursor: 'pointer',
                  border: selectedServerId === server.id ? '2px solid #0073ab' : '1px solid #e0e0e0',
                }}
                onClick={() => setSelectedServerId(selectedServerId === server.id ? null : server.id)}
              >
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '16px' }}>{server.serverName}</h3>
                      <p style={{ margin: '4px 0 0', color: '#666', fontSize: '13px' }}>
                        {server.host}:{server.port}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <McTag
                        appearance={server.serverType === 'POSTGRESQL' ? 'info' : 'warning'}
                      >
                        {server.serverType}
                      </McTag>
                      <McTag
                        appearance={server.status === 'ACTIVE' ? 'success' : 'error'}
                      >
                        {server.status}
                      </McTag>
                    </div>
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: '#888' }}>
                      {server.connectionCount} connection{server.connectionCount !== 1 ? 's' : ''}
                      {' · '}
                      {server.applicationId || 'Unassigned'}
                      {' · '}
                      {server.ownerTeam}
                    </span>
                    {canManage && (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <McButton
                          appearance="neutral"
                          variant="plain"
                          click={(e: any) => {
                            e.stopPropagation();
                            openEditServer(server);
                          }}
                        >
                          <McIcon icon="pencil" />
                        </McButton>
                        {server.status === 'ACTIVE' && (
                          <McButton
                            appearance="neutral"
                            variant="plain"
                            click={(e: any) => {
                              e.stopPropagation();
                              disableServerMutation.mutate(server.id);
                            }}
                          >
                            <McIcon icon="times-circle" />
                          </McButton>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </McCard>
            ))}
            {filteredServers.length === 0 && (
              <p style={{ color: '#888', textAlign: 'center', padding: '40px 0' }}>
                {searchTerm ? 'No servers match your search' : 'No servers registered yet'}
              </p>
            )}
          </div>
        </div>

        {/* Right: Connections */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
              Connections
              {selectedServerId && (
                <span style={{ fontWeight: 400, color: '#666' }}>
                  {' '}— {servers.find((s) => s.id === selectedServerId)?.serverName}
                </span>
              )}
              {' '}({selectedServerConnections.length})
            </h2>
            {canManage && selectedServerId && (
              <McButton appearance="secondary" click={() => openCreateConnection()}>
                <McIcon icon="plus" slot="icon" />
                Add Connection
              </McButton>
            )}
          </div>

          {isLoadingConnections ? (
            <McLoadingIndicator label="Loading connections..." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selectedServerConnections.map((conn) => (
                <McCard key={conn.id}>
                  <div style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '15px' }}>{conn.connectionName}</h3>
                        <p style={{ margin: '4px 0 0', color: '#666', fontSize: '13px' }}>
                          Database: {conn.databaseName}
                          {conn.schemaName && ` · Schema: ${conn.schemaName}`}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <McTag
                          appearance={
                            conn.allowedOps === 'DDL'
                              ? 'warning'
                              : conn.allowedOps === 'READ_WRITE'
                              ? 'info'
                              : 'neutral'
                          }
                        >
                          {conn.allowedOps}
                        </McTag>
                        <McTag appearance={conn.status === 'ACTIVE' ? 'success' : 'error'}>
                          {conn.status}
                        </McTag>
                      </div>
                    </div>
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#888' }}>
                        Pool: {conn.minPoolSize}–{conn.maxPoolSize} · Credential: {conn.credentialType}
                      </span>
                      {canManage && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <McButton
                            appearance="neutral"
                            variant="plain"
                            click={() => openEditConnection(conn)}
                          >
                            <McIcon icon="pencil" />
                          </McButton>
                          {conn.status === 'ACTIVE' && (
                            <McButton
                              appearance="neutral"
                              variant="plain"
                              click={() => disableConnectionMutation.mutate(conn.id)}
                            >
                              <McIcon icon="times-circle" />
                            </McButton>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </McCard>
              ))}
              {selectedServerConnections.length === 0 && (
                <p style={{ color: '#888', textAlign: 'center', padding: '40px 0' }}>
                  {selectedServerId
                    ? 'No connections on this server yet'
                    : 'Select a server to view its connections'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── Server Dialog ──────────────────────────────────── */}
      {showServerDialog && (
        <McDialog
          heading={editingServer ? 'Edit Server' : 'Add Server'}
          open={showServerDialog}
          closing={() => setShowServerDialog(false)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 0' }}>
            <McInput
              label="Server Name"
              placeholder="e.g. PG-EMEA-Prod"
              value={serverForm.serverName}
              onInput={(e: any) => setServerForm({ ...serverForm, serverName: e.target?.value ?? e.detail?.value ?? '' })}
              required
            />
            <McSelect
              label="Server Type"
              value={serverForm.serverType}
              data={[
                { label: 'PostgreSQL', value: 'POSTGRESQL' },
                { label: 'Oracle', value: 'ORACLE' },
              ]}
              optionselected={(e: any) => {
                const selectedType = e.detail?.value || 'POSTGRESQL';
                setServerForm({
                  ...serverForm,
                  serverType: selectedType,
                  port: selectedType === 'ORACLE' ? 1521 : 5432,
                  sslEnabled: selectedType === 'POSTGRESQL' ? false : serverForm.sslEnabled,
                  sslMode: selectedType === 'POSTGRESQL' ? 'disable' : serverForm.sslMode,
                });
              }}
            />
            <McSelect
              label="Application"
              placeholder="Select application"
              value={serverForm.applicationId}
              data={applications.map((app: any) => ({
                label: app.name || app.applicationId,
                value: app.applicationId,
              }))}
              optionselected={(e: any) =>
                setServerForm({ ...serverForm, applicationId: e.detail?.value || '' })
              }
              required
            />
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
              <McInput
                label="Host"
                placeholder="e.g. pg-emea.azure.com"
                value={serverForm.host}
                onInput={(e: any) => setServerForm({ ...serverForm, host: e.target?.value ?? e.detail?.value ?? '' })}
                required
              />
              <McInput
                label="Port"
                type="number"
                value={String(serverForm.port)}
                onInput={(e: any) =>
                  setServerForm({ ...serverForm, port: parseInt(e.target?.value ?? e.detail?.value ?? '5432', 10) })
                }
                required
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <McSelect
                label="SSL"
                value={serverForm.sslEnabled ? 'true' : 'false'}
                data={[
                  { label: 'Disabled', value: 'false' },
                  { label: 'Enabled', value: 'true' },
                ]}
                optionselected={(e: any) => {
                  const enabled = (e.detail?.value || 'false') === 'true';
                  setServerForm({
                    ...serverForm,
                    sslEnabled: enabled,
                    sslMode: enabled ? serverForm.sslMode || 'require' : 'disable',
                  });
                }}
              />
              <McSelect
                label="SSL Mode"
                value={serverForm.sslMode || (serverForm.sslEnabled ? 'require' : 'disable')}
                data={[
                  { label: 'disable', value: 'disable' },
                  { label: 'allow', value: 'allow' },
                  { label: 'prefer', value: 'prefer' },
                  { label: 'require', value: 'require' },
                  { label: 'verify-ca', value: 'verify-ca' },
                  { label: 'verify-full', value: 'verify-full' },
                ]}
                optionselected={(e: any) =>
                  setServerForm({ ...serverForm, sslMode: e.detail?.value || (serverForm.sslEnabled ? 'require' : 'disable') })
                }
                disabled={!serverForm.sslEnabled}
              />
            </div>
            {serverForm.sslEnabled && (
              <McInput
                label="SSL Cert Reference (optional)"
                placeholder="e.g. vault://certs/postgres-root-ca"
                value={serverForm.sslCertRef || ''}
                onInput={(e: any) =>
                  setServerForm({ ...serverForm, sslCertRef: e.target?.value ?? e.detail?.value ?? '' })
                }
              />
            )}
            <McInput
              label="Owner Team"
              placeholder="e.g. CDH-Onboarding"
              value={serverForm.ownerTeam}
              onInput={(e: any) => setServerForm({ ...serverForm, ownerTeam: e.target?.value ?? e.detail?.value ?? '' })}
              required
            />
            <McInput
              label="Description"
              placeholder="Optional description"
              value={serverForm.description || ''}
              onInput={(e: any) => setServerForm({ ...serverForm, description: e.target?.value ?? e.detail?.value ?? '' })}
            />
          </div>
          <McButton
            slot="primaryAction"
            appearance="primary"
            click={handleSaveServer}
            disabled={!serverForm.serverName || !serverForm.applicationId || !serverForm.host || !serverForm.ownerTeam}
            loading={createServerMutation.isPending || updateServerMutation.isPending}
          >
            {editingServer ? 'Update' : 'Create'}
          </McButton>
          <McButton slot="secondaryAction" appearance="neutral" click={() => setShowServerDialog(false)}>
            Cancel
          </McButton>
        </McDialog>
      )}

      {/* ─── Connection Dialog ──────────────────────────────── */}
      {showConnectionDialog && (
        <McDialog
          heading={editingConnection ? 'Edit Connection' : 'Add Connection'}
          open={showConnectionDialog}
          closing={() => setShowConnectionDialog(false)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 0' }}>
            {!editingConnection && (
              <McSelect
                label="Server"
                value={connectionForm.serverId}
                data={servers
                  .filter((s) => s.status === 'ACTIVE')
                  .map((s) => ({
                    label: `${s.serverName} (${s.serverType})`,
                    value: s.id,
                  }))}
                optionselected={(e: any) =>
                  setConnectionForm({ ...connectionForm, serverId: e.detail?.value || '' })
                }
              />
            )}
            <McInput
              label="Connection Name"
              placeholder="e.g. CRMOB-Onboard-DB"
              value={connectionForm.connectionName}
              onInput={(e: any) =>
                setConnectionForm({ ...connectionForm, connectionName: e.target?.value ?? e.detail?.value ?? '' })
              }
              required
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <McInput
                label="Database Name"
                placeholder="e.g. crmob_onboard"
                value={connectionForm.databaseName}
                onInput={(e: any) =>
                  setConnectionForm({ ...connectionForm, databaseName: e.target?.value ?? e.detail?.value ?? '' })
                }
                required
              />
              <McInput
                label="Schema (optional)"
                placeholder="e.g. public"
                value={connectionForm.schemaName || ''}
                onInput={(e: any) =>
                  setConnectionForm({ ...connectionForm, schemaName: e.target?.value ?? e.detail?.value ?? '' })
                }
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
              <McSelect
                label="Credential Type"
                value={connectionForm.credentialType}
                data={[
                  { label: 'Environment Variable', value: 'ENV' },
                  { label: 'Azure Key Vault', value: 'AZURE_KEYVAULT' },
                ]}
                optionselected={(e: any) =>
                  setConnectionForm({ ...connectionForm, credentialType: e.detail?.value || 'ENV' })
                }
              />
              <McInput
                label="Credential Reference"
                placeholder="e.g. CRMOB_ONBOARD or vault://path"
                value={connectionForm.credentialRef}
                onInput={(e: any) =>
                  setConnectionForm({ ...connectionForm, credentialRef: e.target?.value ?? e.detail?.value ?? '' })
                }
                required
              />
            </div>
            <McSelect
              label="Allowed Operations"
              value={connectionForm.allowedOps}
              data={[
                { label: 'Read Only', value: 'READ_ONLY' },
                { label: 'Read & Write', value: 'READ_WRITE' },
                { label: 'DDL (Full Access)', value: 'DDL' },
              ]}
              optionselected={(e: any) =>
                setConnectionForm({ ...connectionForm, allowedOps: e.detail?.value || 'READ_ONLY' })
              }
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <McInput
                label="Min Pool Size"
                type="number"
                value={String(connectionForm.minPoolSize ?? 0)}
                onInput={(e: any) =>
                  setConnectionForm({
                    ...connectionForm,
                    minPoolSize: parseInt(e.target?.value ?? e.detail?.value ?? '0', 10),
                  })
                }
              />
              <McInput
                label="Max Pool Size"
                type="number"
                value={String(connectionForm.maxPoolSize ?? 3)}
                onInput={(e: any) =>
                  setConnectionForm({
                    ...connectionForm,
                    maxPoolSize: parseInt(e.target?.value ?? e.detail?.value ?? '3', 10),
                  })
                }
              />
            </div>
            <McInput
              label="Description"
              placeholder="Optional description"
              value={connectionForm.description || ''}
              onInput={(e: any) =>
                setConnectionForm({ ...connectionForm, description: e.target?.value ?? e.detail?.value ?? '' })
              }
            />
          </div>
          <McButton
            slot="primaryAction"
            appearance="primary"
            click={handleSaveConnection}
            disabled={
              !connectionForm.serverId ||
              !connectionForm.connectionName ||
              !connectionForm.databaseName ||
              !connectionForm.credentialRef
            }
            loading={createConnectionMutation.isPending || updateConnectionMutation.isPending}
          >
            {editingConnection ? 'Update' : 'Create'}
          </McButton>
          <McButton
            slot="secondaryAction"
            appearance="neutral"
            click={() => setShowConnectionDialog(false)}
          >
            Cancel
          </McButton>
        </McDialog>
      )}
    </div>
  );
}
